import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { ChatService } from '@/services/chat.service';
import type { ChatMessage, CreateChatMessageData } from '@/types';
import { useFirestoreSubscription } from './useFirestoreSubscription';

interface UseChatResult {
    messages: ChatMessage[];
    isLoading: boolean;
    error: Error | null;
    sendMessage: (content: string, type?: 'message' | 'system') => Promise<void>;
}

export function useChat(playlistId?: string): UseChatResult {
    const { user } = useAuth();

    const { data, isLoading, error } = useFirestoreSubscription<ChatMessage[]>(
        (callback) => {
            if (!playlistId) {
                callback([]);
                return () => { };
            }
            return ChatService.subscribeToChat(playlistId, callback);
        },
        [playlistId]
    );

    const sendMessage = useCallback(async (content: string, type: 'message' | 'system' = 'message') => {
        if (!playlistId || !user) return;

        const messageData: CreateChatMessageData = {
            userId: user.id,
            userName: user.name,
            userAvatar: user.avatar,
            message: content,
            type
        };

        await ChatService.sendMessage(playlistId, messageData);
    }, [playlistId, user]);

    return {
        messages: data || [],
        isLoading,
        error,
        sendMessage
    };
}
